// MediaPipe Face Detection utilities
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
let faceLandmarker = null;
let isInitialized = false;
/**
 * MediaPipe FaceLandmarkerを初期化
 */
export async function initializeFaceLandmarker() {
    if (isInitialized) {
        return;
    }
    try {
        const vision = await FilesetResolver.forVisionTasks('https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm');
        faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
            baseOptions: {
                modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
                delegate: 'GPU'
            },
            outputFaceBlendshapes: true,
            outputFacialTransformationMatrixes: false,
            numFaces: 1,
            runningMode: 'VIDEO'
        });
        isInitialized = true;
    }
    catch (error) {
        console.error('Failed to initialize FaceLandmarker:', error);
        throw error;
    }
}
/**
 * ビデオフレームから顔データを抽出
 */
export async function detectFaceData(videoElement, timestamp) {
    if (!faceLandmarker || !isInitialized) {
        await initializeFaceLandmarker();
    }
    if (!faceLandmarker) {
        return null;
    }
    try {
        const results = faceLandmarker.detectForVideo(videoElement, timestamp);
        if (!results.faceBlendshapes || results.faceBlendshapes.length === 0) {
            return null;
        }
        const blendshapes = results.faceBlendshapes[0];
        const faceData = extractFaceParameters(blendshapes.categories);
        return faceData;
    }
    catch (error) {
        console.error('Face detection error:', error);
        return null;
    }
}
/**
 * BlendShapeから顔パラメータを抽出
 */
function extractFaceParameters(categories) {
    const getShapeValue = (name) => {
        const category = categories.find(c => c.categoryName === name);
        return category ? Math.round(category.score * 100) / 100 : 0;
    };
    const faceData = {
        timestamp: Date.now(),
        eyeBlinkLeft: getShapeValue('eyeBlinkLeft'),
        eyeBlinkRight: getShapeValue('eyeBlinkRight'),
        eyeWideLeft: getShapeValue('eyeWideLeft'),
        eyeWideRight: getShapeValue('eyeWideRight'),
        jawOpen: getShapeValue('jawOpen'),
        mouthSmile: Math.max(getShapeValue('mouthSmileLeft'), getShapeValue('mouthSmileRight')),
        mouthPucker: getShapeValue('mouthPucker'),
        mouthRollLower: getShapeValue('mouthRollLower'),
        browInnerUp: getShapeValue('browInnerUp'),
        browOuterUp: Math.max(getShapeValue('browOuterUpLeft'), getShapeValue('browOuterUpRight')),
        browDown: Math.max(getShapeValue('browDownLeft'), getShapeValue('browDownRight'))
    };
    // 簡単な感情推定
    faceData.emotionScores = estimateEmotions(faceData);
    faceData.dominantEmotion = getDominantEmotion(faceData.emotionScores);
    return faceData;
}
/**
 * 感情を推定
 */
function estimateEmotions(data) {
    const emotions = {
        happy: 0,
        surprised: 0,
        sad: 0,
        angry: 0,
        neutral: 0
    };
    // 笑顔の検出
    emotions.happy = Math.min(1, data.mouthSmile * 1.5);
    // 驚きの検出
    emotions.surprised = Math.min(1, (data.eyeWideLeft + data.eyeWideRight) / 2 +
        data.browInnerUp * 0.5 +
        data.jawOpen * 0.3);
    // 悲しみの検出
    emotions.sad = Math.min(1, data.browInnerUp * 0.7 +
        (1 - data.mouthSmile) * 0.3);
    // 怒りの検出
    emotions.angry = Math.min(1, data.browDown * 1.2);
    // 中立の検出
    const totalEmotions = emotions.happy + emotions.surprised + emotions.sad + emotions.angry;
    emotions.neutral = Math.max(0, 1 - totalEmotions);
    // 正規化
    const sum = Object.values(emotions).reduce((a, b) => a + b, 0);
    if (sum > 0) {
        Object.keys(emotions).forEach(key => {
            emotions[key] = Math.round(emotions[key] / sum * 100) / 100;
        });
    }
    return emotions;
}
/**
 * 最も強い感情を取得
 */
function getDominantEmotion(emotions) {
    return Object.entries(emotions)
        .reduce((a, b) => a[1] > b[1] ? a : b)[0];
}
/**
 * クリーンアップ
 */
export function cleanupFaceLandmarker() {
    if (faceLandmarker) {
        faceLandmarker.close();
        faceLandmarker = null;
        isInitialized = false;
    }
}
